import { log } from './production-logger';

import {
  Organization,
  OrganizationFormData,
  OrganizationSettings,
  OrganizationMember,
  OrganizationRole,
  OrganizationInvitation,
  OrganizationDepartment,
  OrganizationProject,
  OrganizationCostCenter,
  OrganizationLocation,
  SubscriptionPlan,
  SubscriptionStatus,
} from '../models/organization';
import { logEvent } from './analytics-service';
import {
  doc,
  collection,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  addDoc,
  serverTimestamp,
  Timestamp,
  runTransaction,
  limit,
  orderBy,
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes, deleteObject, uploadString } from 'firebase/storage';
// Import organizationMembershipService dynamically to avoid circular dependency
import { User } from '../models/user';
import * as FileSystem from 'expo-file-system';
import { Platform } from 'react-native';
import { auth, firestore, storage } from '../firebase/firebase';
import { v4 as uuidv4 } from 'uuid';

/**
 * Default organization settings
 */
const DEFAULT_SETTINGS: OrganizationSettings = {
  defaultCurrency: 'VND',
  defaultLanguage: 'vi',
  fiscalYearStartMonth: 1,
  timezone: 'Asia/Ho_Chi_Minh',
  dateFormat: 'DD/MM/YYYY',
  invoiceNumberFormat: 'INV-{YYYY}-{0000}',
  defaultInvoiceDueDays: 30,
  privacySettings: {
    allowMemberDataSharing: true,
    allowAnonymousStatistics: true,
  },
  notificationSettings: {
    invoiceCreated: true,
    paymentReceived: true,
    paymentOverdue: true,
    subscriptionRenewal: true,
    memberActivity: true,
  },
  featureFlags: {
    invoiceExtraction: true,
    supplierManagement: true,
  },
};

/**
 * Default subscription
 */
const DEFAULT_SUBSCRIPTION = {
  plan: 'free',
  status: 'active',
  currentPeriodStart: new Date().toISOString(),
  currentPeriodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days from now
};

/**
 * Service for managing organizations
 */
class OrganizationService {
  /**
   * Create a new organization
   * @param data Organization data
   * @returns The created organization
   */
  async createOrganization(data: Partial<Organization>): Promise<Organization | null> {
    try {
      // Direct usage of auth instance
      const currentUser = auth.currentUser;

      if (!currentUser || !currentUser.uid) {
        throw new Error('User not authenticated');
      }

      const orgData = {
        name: data.name || 'New Organization',
        taxId: data.taxId || '',
        email: data.email || '',
        phone: data.phone || '',
        address: data.address || {},
        logo: data.logo || '',
        settings: data.settings || DEFAULT_SETTINGS,
        subscription: data.subscription || DEFAULT_SUBSCRIPTION,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: currentUser.uid,
        isDeleted: false,
      };

      // Create the organization
      // Direct usage of firestore instance
      const docRef = await addDoc(collection(firestore, 'organizations'), orgData);

      // Create membership for creator as admin
      const { default: organizationMembershipService } = await import('./organization-membership-service');
      await organizationMembershipService.createUserMembership({
        userId: currentUser.uid,
        organizationId: docRef.id,
        role: 'admin',
        isPrimary: true, // Make this the primary organization
      });

      // Get the created organization
      return this.getOrganizationById(docRef.id);
    } catch (error) {
      log.error('Error creating organization:', error);
      return null;
    }
  }

  /**
   * Get an organization by ID
   * @param id Organization ID
   * @returns The organization or null if not found
   */
  async getOrganizationById(id: string): Promise<Organization | null> {
    try {
      // Direct usage of firestore instance
      const orgRef = doc(firestore, 'organizations', id);
      const orgDoc = await getDoc(orgRef);

      if (orgDoc.exists()) {
        const data = orgDoc.data();
        return {
          id: orgDoc.id,
          ...(data as Omit<Organization, 'id'>),
        };
      }

      return null;
    } catch (error) {
      log.error('Error getting organization:', error);
      return null;
    }
  }

  /**
   * Update an organization
   * @param id Organization ID
   * @param data Organization data to update
   * @returns The updated organization
   */
  async updateOrganization(id: string, data: Partial<Organization>): Promise<Organization | null> {
    try {
      // Direct usage of firestore instance
      const orgRef = doc(firestore, 'organizations', id);

      // Create a clean update object without undefined values
      const cleanData: Record<string, any> = {};

      // Handle top-level properties
      Object.entries(data).forEach(([key, value]) => {
        if (value === undefined) {
          // Skip undefined values
          return;
        }

        if (value === null) {
          // Firebase accepts null
          cleanData[key] = null;
          return;
        }

        if (typeof value === 'object') {
          // Handle nested objects like bankDetails
          const cleanObject: Record<string, any> = {};
          let hasValues = false;

          Object.entries(value).forEach(([nestedKey, nestedValue]) => {
            if (nestedValue !== undefined) {
              cleanObject[nestedKey] = nestedValue;
              hasValues = true;
            }
          });

          // Only include non-empty objects
          if (hasValues) {
            cleanData[key] = cleanObject;
          } else {
            // If the object is empty, use null
            cleanData[key] = null;
          }
        } else {
          // Regular value
          cleanData[key] = value;
        }
      });

      // Always add updatedAt
      cleanData.updatedAt = new Date().toISOString();

      // Update the document with clean data
      await updateDoc(orgRef, cleanData);

      // Fetch and return the updated organization
      return this.getOrganizationById(id);
    } catch (error) {
      log.error('Error updating organization:', error);
      throw error;
    }
  }

  /**
   * Get all organizations for the current user
   * @returns List of organizations
   */
  async getUserOrganizations(): Promise<Organization[]> {
    try {
      // Direct usage of auth instance
      const currentUser = auth.currentUser;

      if (!currentUser || !currentUser.uid) {
        return [];
      }

      // Get user's memberships
      const { default: organizationMembershipService } = await import('./organization-membership-service');
      const memberships = await organizationMembershipService.getUserMemberships(currentUser.uid);

      // Get organizations for each membership
      const organizations: Organization[] = [];

      for (const membership of memberships) {
        const organization = await this.getOrganizationById(membership.organizationId);
        if (organization) {
          organizations.push(organization);
        }
      }

      return organizations;
    } catch (error) {
      log.error('Error getting user organizations:', error);
      return [];
    }
  }

  /**
   * Format Firestore timestamp to ISO string
   * @param timestamp Firestore timestamp
   * @returns ISO string date
   */
  private formatTimestamp(timestamp: Timestamp | null | undefined): string {
    if (!timestamp) {
      return new Date().toISOString();
    }
    return timestamp.toDate().toISOString();
  }

  /**
   * Get organization by owner ID
   * @param ownerId - User ID of the organization owner
   * @returns Promise with organization data
   */
  async getOrganizationByOwnerId(ownerId: string): Promise<Organization | null> {
    try {
      // Direct usage of firestore instance
      const orgsCollection = collection(firestore, 'organizations');
      const q = query(orgsCollection, where('ownerId', '==', ownerId));

      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const orgDoc = querySnapshot.docs[0];
        return { id: orgDoc.id, ...orgDoc.data() } as Organization;
      }

      return null;
    } catch (error) {
      log.error('Error getting organization by owner:', error);
      throw new Error('Failed to get organization by owner');
    }
  }

  /**
   * Get organization by member ID
   * @param memberId - User ID of a member
   * @returns Promise with organization data
   */
  async getOrganizationByMemberId(memberId: string): Promise<Organization | null> {
    try {
      // Direct usage of firestore instance
      const orgsCollection = collection(firestore, 'organizations');
      const q = query(orgsCollection, where('memberIds', 'array-contains', memberId));

      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const orgDoc = querySnapshot.docs[0];
        return { id: orgDoc.id, ...orgDoc.data() } as Organization;
      }

      return null;
    } catch (error) {
      log.error('Error getting organization by member:', error);
      throw new Error('Failed to get organization by member');
    }
  }

  /**
   * Upload organization logo
   * @param orgId - Organization ID
   * @param imageUri - Image URI
   */
  async uploadOrganizationLogo(orgId: string, imageUri: string): Promise<string> {
    try {
      // Direct usage of storage instance
      // Generate storage path
      const fileName = `logo_${Date.now()}.jpg`;
      const storageRef = ref(storage, `organizations/${orgId}/${fileName}`);

      // Prepare metadata
      const metadata = {
        contentType: 'image/jpeg',
        customMetadata: {
          organizationId: orgId,
          uploadedAt: new Date().toISOString(),
          type: 'logo',
        },
      };

      let downloadURL: string;
      let uploadMethod = 'unknown';

      // Platform-specific upload logic following proven patterns from profile photo
      if (Platform.OS === 'web') {
        log.debug('Web platform: attempting organization logo upload');
        try {
          // Primary method for web: fetch and uploadBytes
          uploadMethod = 'web-upload-bytes';
          const response = await fetch(imageUri);
          const blob = await response.blob();

          log.debug('Web: uploading blob to Firebase Storage');
          const snapshot = await uploadBytes(storageRef, blob, metadata);
          downloadURL = await getDownloadURL(snapshot.ref);
        } catch (webError) {
          log.error('Web uploadBytes failed, trying data URL fallback:', webError);
          try {
            // Fallback: try data URL upload if blob fails
            uploadMethod = 'web-data-url-fallback';
            const snapshot = await uploadString(storageRef, imageUri, 'data_url', metadata);
            downloadURL = await getDownloadURL(snapshot.ref);
          } catch (dataUrlError) {
            log.error('Web data URL fallback also failed:', dataUrlError);
            throw new Error('Both web upload methods failed');
          }
        }
      } else {
        // Android and other platforms
        log.debug('Android/Other platform: attempting base64 upload');
        try {
          // Standard approach for Android
          uploadMethod = 'android-base64';
          const FileSystem = require('expo-file-system');
          const base64Data = await FileSystem.readAsStringAsync(imageUri, {
            encoding: FileSystem.EncodingType.Base64,
          });

          // Upload using base64 string directly (avoiding potential ArrayBuffer issues)
          const snapshot = await uploadString(storageRef, base64Data, 'base64', metadata);
          downloadURL = await getDownloadURL(snapshot.ref);
          log.debug('Android base64 upload completed successfully');
        } catch (androidError) {
          log.error('Android base64 upload failed, trying data URL:', androidError);
          try {
            // Fallback: use data URL method
            uploadMethod = 'android-data-url-fallback';
            const FileSystem = require('expo-file-system');
            const base64Data = await FileSystem.readAsStringAsync(imageUri, {
              encoding: FileSystem.EncodingType.Base64,
            });

            const dataUrl = `data:image/jpeg;base64,${base64Data}`;
            const snapshot = await uploadString(storageRef, dataUrl, 'data_url', metadata);
            downloadURL = await getDownloadURL(snapshot.ref);
            log.debug('Android data URL fallback completed successfully');
          } catch (fallbackError) {
            log.error('Android fallback also failed:', fallbackError);
            throw new Error('All Android upload methods failed');
          }
        }
      }

      log.debug(`Successfully uploaded organization logo using method: ${uploadMethod}`);
      log.debug('Download URL obtained:', downloadURL);

      // Update organization with logo URL
      await this.updateOrganization(orgId, { logo: downloadURL });

      return downloadURL;
    } catch (error) {
      log.error('Error uploading organization logo:', error);
      throw error;
    }
  }

  /**
   * Delete organization logo
   * @param organizationId - Organization ID
   * @returns Promise<void>
   */
  async deleteLogo(organizationId: string): Promise<void> {
    try {
      logEvent('delete_organization_logo', { organizationId });
      // Direct usage of firestore and storage instances
      // Get the organization to check if it has a logo
      const orgDoc = await getDoc(doc(firestore, 'organizations', organizationId));

      if (!orgDoc.exists()) {
        throw new Error('Organization not found');
      }

      const org = orgDoc.data() as Organization;

      if (org.settings?.logoUrl) {
        // Delete the file from storage
        const logoRef = ref(storage, `organizations/${organizationId}/logo`);
        await deleteObject(logoRef);

        // Update the organization document to remove the logo URL
        await this.updateOrganization(organizationId, {
          settings: {
            ...org.settings,
            logoUrl: '',
          },
        });
      }
    } catch (error) {
      log.error('Error deleting logo:', error);
      throw new Error('Failed to delete logo');
    }
  }

  /**
   * Add a member to the organization
   * @param organizationId - Organization ID
   * @param userId - User ID to add
   * @param role - Member role
   * @param invitedBy - User ID of the inviter
   * @returns Promise<void>
   */
  async addMember(
    organizationId: string,
    userId: string,
    role: OrganizationRole,
    invitedBy?: string,
  ): Promise<void> {
    try {
      logEvent('add_organization_member', { organizationId, userId, role });

      const now = new Date().toISOString();
      // Direct usage of firestore instance

      // Run as a transaction to ensure consistency
      await runTransaction(firestore, async transaction => {
        // Get the organization
        const orgRef = doc(firestore, 'organizations', organizationId);
        const orgDoc = await transaction.get(orgRef);

        if (!orgDoc.exists()) {
          throw new Error('Organization not found');
        }

        const org = orgDoc.data() as Organization;

        // Check if user is already a member
        if (org.memberIds?.includes(userId)) {
          throw new Error('User is already a member of this organization');
        }

        // Add user to members array
        const updatedMemberIds = [...(org.memberIds || []), userId];

        // Update the organization document
        transaction.update(orgRef, {
          memberIds: updatedMemberIds,
          updatedAt: now,
        });

        // Create member document
        const memberRef = doc(firestore, 'organizations', organizationId, 'members', userId);
        const member = {
          userId,
          role,
          joinedAt: now,
          invitedBy,
        };

        transaction.set(memberRef, member);

        // Update the user document with the organization ID
        const userRef = doc(firestore, 'users', userId);
        transaction.update(userRef, {
          organizationId,
          updatedAt: now,
        });
      });
    } catch (error) {
      log.error('Error adding member:', error);
      throw new Error('Failed to add member to organization');
    }
  }

  /**
   * Update member role
   * @param organizationId - Organization ID
   * @param userId - User ID to update
   * @param newRole - New role for the user
   * @returns Promise<void>
   */
  async updateMemberRole(
    organizationId: string,
    userId: string,
    newRole: OrganizationRole,
  ): Promise<void> {
    try {
      logEvent('update_organization_member_role', { organizationId, userId, newRole });

      // Direct usage of firestore instance
      // Check if the user is trying to update the owner role
      const orgDoc = await getDoc(doc(firestore, 'organizations', organizationId));

      if (!orgDoc.exists()) {
        throw new Error('Organization not found');
      }

      const org = orgDoc.data() as Organization;

      if (userId === org.ownerId && newRole !== OrganizationRole.OWNER) {
        throw new Error('Cannot change the role of the organization owner');
      }

      // Update the member document
      const memberRef = doc(firestore, 'organizations', organizationId, 'members', userId);
      await updateDoc(memberRef, {
        role: newRole,
      });
    } catch (error) {
      log.error('Error updating member role:', error);
      throw new Error('Failed to update member role');
    }
  }

  /**
   * Remove a member from the organization
   * @param organizationId - Organization ID
   * @param userId - User ID to remove
   * @returns Promise<void>
   */
  async removeMember(organizationId: string, userId: string): Promise<void> {
    try {
      logEvent('remove_organization_member', { organizationId, userId });

      // Direct usage of firestore instance
      // Run as a transaction to ensure consistency
      await runTransaction(firestore, async transaction => {
        // Get the organization
        const orgRef = doc(firestore, 'organizations', organizationId);
        const orgDoc = await transaction.get(orgRef);

        if (!orgDoc.exists()) {
          throw new Error('Organization not found');
        }

        const org = orgDoc.data() as Organization;

        // Check if user is the owner
        if (userId === org.ownerId) {
          throw new Error('Cannot remove the organization owner');
        }

        // Check if user is a member
        if (!org.memberIds?.includes(userId)) {
          throw new Error('User is not a member of this organization');
        }

        // Remove user from members array
        const updatedMemberIds = org.memberIds?.filter(id => id !== userId) || [];

        // Update the organization document
        transaction.update(orgRef, {
          memberIds: updatedMemberIds,
          updatedAt: new Date().toISOString(),
        });

        // Delete member document
        const memberRef = doc(firestore, 'organizations', organizationId, 'members', userId);
        transaction.delete(memberRef);

        // Update the user document to remove the organization ID
        const userRef = doc(firestore, 'users', userId);
        transaction.update(userRef, {
          organizationId: null,
          updatedAt: new Date().toISOString(),
        });
      });
    } catch (error) {
      log.error('Error removing member:', error);
      throw new Error('Failed to remove member from organization');
    }
  }

  /**
   * Get members of an organization
   * @param organizationId - Organization ID
   */
  async getOrganizationMembers(organizationId: string): Promise<OrganizationMember[]> {
    try {
      // Direct usage of firestore instance
      // Query memberships (don't filter by isActive to handle legacy data)
      const membershipsQuery = query(
        collection(firestore, 'memberships'),
        where('organizationId', '==', organizationId),
      );

      const membershipsSnapshot = await getDocs(membershipsQuery);
      const memberships: OrganizationMember[] = [];

      // Get user details for each membership
      for (const membershipDoc of membershipsSnapshot.docs) {
        const membershipData = membershipDoc.data();

        // Skip inactive memberships (handle both undefined and false)
        if (membershipData.isActive === false) {
          continue;
        }

        try {
          // Try to get user document
          const userDoc = await getDoc(doc(firestore, 'users', membershipData.userId));

          if (userDoc.exists()) {
            const userData = userDoc.data() as User;

            memberships.push({
              id: membershipDoc.id,
              userId: membershipData.userId,
              organizationId: membershipData.organizationId,
              role: membershipData.role,
              isPrimary: membershipData.isPrimary || false,
              joinedAt: membershipData.joinedAt,
              user: {
                id: userDoc.id,
                displayName: userData.displayName,
                email: userData.email,
                photoURL: userData.photoURL,
              },
            });
          } else {
            // User document doesn't exist, create minimal member record
            memberships.push({
              id: membershipDoc.id,
              userId: membershipData.userId,
              organizationId: membershipData.organizationId,
              role: membershipData.role,
              isPrimary: membershipData.isPrimary || false,
              joinedAt: membershipData.joinedAt,
              user: {
                id: membershipData.userId,
                displayName: 'Unknown User',
                email: membershipData.email || 'No email available',
                photoURL: undefined,
              },
            });
          }
        } catch (userError) {
          log.warn(`Could not fetch user details for ${membershipData.userId}:`, userError);
          
          // Handle permission errors gracefully by creating minimal member record
          memberships.push({
            id: membershipDoc.id,
            userId: membershipData.userId,
            organizationId: membershipData.organizationId,
            role: membershipData.role,
            isPrimary: membershipData.isPrimary || false,
            joinedAt: membershipData.joinedAt,
            user: {
              id: membershipData.userId,
              displayName: membershipData.displayName || 'Unknown User',
              email: membershipData.email || 'No email available',
              photoURL: membershipData.photoURL,
            },
          });
        }
      }

      return memberships;
    } catch (error) {
      log.error('Error getting organization members:', error);
      return [];
    }
  }

  /**
   * Create an invitation to join an organization
   * @param organizationId - Organization ID
   * @param email - Email of the user to invite
   * @param role - Role to assign to the user
   * @param invitedBy - User ID of the inviter
   * @param message - Optional invitation message
   * @returns Promise with the created invitation
   */
  async createInvitation(
    organizationId: string,
    email: string,
    role: OrganizationRole,
    invitedBy: string,
    message?: string,
  ): Promise<any> {
    try {
      logEvent('create_organization_invitation', { organizationId, email, role });

      // Check if user is already a member
      const existingMembership = await this.checkExistingMembership(organizationId, email);
      if (existingMembership) {
        throw new Error('User is already a member of this organization');
      }

      // Check for existing pending invitation
      const existingInvitation = await this.getPendingInvitation(organizationId, email);
      if (existingInvitation) {
        throw new Error('An invitation for this email is already pending');
      }

      // Current timestamp
      const now = new Date().toISOString();

      // Expiry date (7 days from now)
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + 7);

      // Generate secure token
      const token = this.generateInvitationToken();

      // Create invitation object
      const invitation = {
        organizationId,
        email: email.toLowerCase().trim(),
        role,
        invitedBy,
        createdAt: now,
        expiresAt: expiryDate.toISOString(),
        status: 'pending',
        token,
        message,
      };

      // Direct usage of firestore instance
      // Add the invitation to Firestore (organization sub-collection for better security)
      const invitationRef = await addDoc(
        collection(firestore, 'organizations', organizationId, 'invitations'),
        invitation,
      );

      // Log audit event
      await this.logAuditEvent(organizationId, {
        action: 'invitation_created',
        performedBy: invitedBy,
        targetEmail: email,
        details: { role, message },
      });

      // TODO: Send invitation email
      await this.sendInvitationEmail(organizationId, email, role, token, message);

      // Return the complete invitation with ID
      return {
        id: invitationRef.id,
        ...invitation,
      };
    } catch (error) {
      log.error('Error creating invitation:', error);
      throw error;
    }
  }

  /**
   * Accept an organization invitation
   * @param token - Invitation token
   * @param userId - User ID accepting the invitation
   * @returns Promise with the created membership
   */
  async acceptInvitation(token: string, userId: string): Promise<any> {
    try {
      // Direct usage of firestore instance
      // Find invitation by token
      const invitation = await this.findInvitationByToken(token);
      if (!invitation) {
        throw new Error('Invalid or expired invitation');
      }

      // Check if invitation is still valid
      if (invitation.status !== 'pending') {
        throw new Error('Invitation is no longer valid');
      }

      if (new Date(invitation.expiresAt) < new Date()) {
        throw new Error('Invitation has expired');
      }

      // Get user details
      const userDoc = await getDoc(doc(firestore, 'users', userId));
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      const userData = userDoc.data();

      // Verify email matches
      if (userData.email.toLowerCase() !== invitation.email.toLowerCase()) {
        throw new Error('Email address does not match invitation');
      }

      // Create organization membership
      const { default: organizationMembershipService } = await import('./organization-membership-service');
      const membership = await organizationMembershipService.createMembership(
        userId,
        invitation.organizationId,
        invitation.role,
        false, // Not primary by default
        invitation.invitedBy,
      );

      // Update invitation status
      await updateDoc(doc(firestore, 'organizations', invitation.organizationId, 'invitations', invitation.id), {
        status: 'accepted',
        acceptedAt: new Date().toISOString(),
      });

      // Log audit event
      await this.logAuditEvent(invitation.organizationId, {
        action: 'invitation_accepted',
        performedBy: userId,
        targetEmail: invitation.email,
        details: { role: invitation.role },
      });

      logEvent('organization_invitation_accepted', {
        organizationId: invitation.organizationId,
        userId,
        role: invitation.role,
      });

      return membership;
    } catch (error) {
      log.error('Error accepting invitation:', error);
      throw error;
    }
  }

  /**
   * Reject an organization invitation
   * @param token - Invitation token
   * @param userId - User ID rejecting the invitation
   */
  async rejectInvitation(token: string, userId: string): Promise<void> {
    try {
      // Direct usage of firestore instance
      // Find invitation by token
      const invitation = await this.findInvitationByToken(token);
      if (!invitation) {
        throw new Error('Invalid invitation');
      }

      // Update invitation status
      await updateDoc(doc(firestore, 'organizations', invitation.organizationId, 'invitations', invitation.id), {
        status: 'rejected',
        rejectedAt: new Date().toISOString(),
      });

      // Log audit event
      await this.logAuditEvent(invitation.organizationId, {
        action: 'invitation_rejected',
        performedBy: userId,
        targetEmail: invitation.email,
        details: { role: invitation.role },
      });

      logEvent('organization_invitation_rejected', {
        organizationId: invitation.organizationId,
        userId,
        role: invitation.role,
      });
    } catch (error) {
      log.error('Error rejecting invitation:', error);
      throw error;
    }
  }

  /**
   * Get pending invitations for an organization
   * @param organizationId - Organization ID
   * @returns Promise with array of invitations
   */
  async getOrganizationInvitations(organizationId: string): Promise<any[]> {
    try {
      // Direct usage of firestore instance
      const invitationsQuery = query(
        collection(firestore, 'organizations', organizationId, 'invitations'),
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc'),
      );

      const snapshot = await getDocs(invitationsQuery);
      const invitations: any[] = [];

      snapshot.forEach(doc => {
        const data = doc.data();
        invitations.push({
          id: doc.id,
          ...data,
          isExpired: new Date(data.expiresAt) < new Date(),
        });
      });

      return invitations;
    } catch (error) {
      log.error('Error getting organization invitations:', error);
      return [];
    }
  }

  /**
   * Cancel an invitation
   * @param organizationId - Organization ID
   * @param invitationId - Invitation ID
   * @param cancelledBy - User ID cancelling the invitation
   */
  async cancelInvitation(organizationId: string, invitationId: string, cancelledBy: string): Promise<void> {
    try {
      // Direct usage of firestore instance
      await updateDoc(doc(firestore, 'organizations', organizationId, 'invitations', invitationId), {
        status: 'cancelled',
        cancelledAt: new Date().toISOString(),
        cancelledBy,
      });

      // Log audit event
      await this.logAuditEvent(organizationId, {
        action: 'invitation_cancelled',
        performedBy: cancelledBy,
        details: { invitationId },
      });

      logEvent('organization_invitation_cancelled', {
        organizationId,
        invitationId,
        cancelledBy,
      });
    } catch (error) {
      log.error('Error cancelling invitation:', error);
      throw error;
    }
  }

  // Helper methods

  private async checkExistingMembership(organizationId: string, email: string): Promise<boolean> {
    try {
      // Direct usage of firestore instance
      // Find user by email
      const usersQuery = query(
        collection(firestore, 'users'),
        where('email', '==', email.toLowerCase().trim()),
        limit(1),
      );

      const userSnapshot = await getDocs(usersQuery);
      if (userSnapshot.empty) {
        return false;
      }

      const userId = userSnapshot.docs[0].id;

      // Check if membership exists
      const { default: organizationMembershipService } = await import('./organization-membership-service');
      const membership = await organizationMembershipService.getUserMembershipInOrganization(userId, organizationId);
      return !!membership;
    } catch (error) {
      log.error('Error checking existing membership:', error);
      return false;
    }
  }

  private async getPendingInvitation(organizationId: string, email: string): Promise<any> {
    try {
      // Direct usage of firestore instance
      const invitationsQuery = query(
        collection(firestore, 'organizations', organizationId, 'invitations'),
        where('email', '==', email.toLowerCase().trim()),
        where('status', '==', 'pending'),
        limit(1),
      );

      const snapshot = await getDocs(invitationsQuery);
      if (snapshot.empty) {
        return null;
      }

      return {
        id: snapshot.docs[0].id,
        ...snapshot.docs[0].data(),
      };
    } catch (error) {
      log.error('Error getting pending invitation:', error);
      return null;
    }
  }

  private async findInvitationByToken(token: string): Promise<any> {
    try {
      // Direct usage of firestore instance
      // Since we can't query across subcollections easily, we need to implement a different approach
      // For now, we'll store invitations in a top-level collection with organization reference
      const invitationsQuery = query(
        collection(firestore, 'invitations'),
        where('token', '==', token),
        limit(1),
      );

      const snapshot = await getDocs(invitationsQuery);
      if (snapshot.empty) {
        return null;
      }

      return {
        id: snapshot.docs[0].id,
        ...snapshot.docs[0].data(),
      };
    } catch (error) {
      log.error('Error finding invitation by token:', error);
      return null;
    }
  }

  private generateInvitationToken(): string {
    // Generate a secure random token
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let token = '';
    for (let i = 0; i < 32; i++) {
      token += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return token;
  }

  private async sendInvitationEmail(
    organizationId: string,
    email: string,
    role: OrganizationRole,
    token: string,
    message?: string,
  ): Promise<void> {
    try {
      // Get organization details
      const org = await this.getOrganizationById(organizationId);
      if (!org) {
        throw new Error('Organization not found');
      }

      // Get inviter details
      const inviterDoc = await getDoc(doc(firestore, 'users', org.ownerId));
      const inviterName = inviterDoc.exists() ? inviterDoc.data().displayName : 'Team Admin';

      // Generate invitation URL based on environment
      const baseUrl = process.env.EXPO_PUBLIC_WEB_URL || 'https://app.invoiceai.com';
      const invitationUrl = `${baseUrl}/accept-invitation?token=${token}`;

      // Use email service to send invitation
      const { emailService } = await import('./email-service');
      const success = await emailService.sendOrganizationInvitation(
        email,
        inviterName,
        org.name,
        token,
        invitationUrl
      );

      if (!success) {
        log.warn('Failed to send invitation email via email service');
      }

      logEvent('invitation_email_sent', { organizationId, email, role, success });
    } catch (error) {
      log.error('Error sending invitation email:', error);
      // Don't throw - allow invitation to be created even if email fails
    }
  }

  private async logAuditEvent(organizationId: string, event: any): Promise<void> {
    try {
      // Direct usage of firestore instance
      await addDoc(
        collection(firestore, 'organizations', organizationId, 'audit_logs'),
        {
          ...event,
          timestamp: new Date().toISOString(),
        },
      );
    } catch (error) {
      log.error('Error logging audit event:', error);
    }
  }

  /**
   * Get active invitations for an organization
   * @param organizationId - Organization ID
   * @returns Promise with array of active invitations
   */
  async getActiveInvitations(organizationId: string): Promise<OrganizationInvitation[]> {
    try {
      // Direct usage of firestore instance
      const invitationsCollection = collection(firestore, 'organization_invitations');
      const q = query(
        invitationsCollection,
        where('organizationId', '==', organizationId),
        where('status', '==', 'pending'),
      );

      const querySnapshot = await getDocs(q);

      const invitations: OrganizationInvitation[] = [];

      querySnapshot.forEach(doc => {
        invitations.push({
          id: doc.id,
          ...doc.data(),
        } as OrganizationInvitation);
      });

      return invitations;
    } catch (error) {
      log.error('Error getting active invitations:', error);
      throw new Error('Failed to get active invitations');
    }
  }

  /**
   * Check if an organization name is available
   * @param name - Organization name to check
   * @returns Promise<boolean> - True if the name is available
   */
  async isOrganizationNameAvailable(name: string): Promise<boolean> {
    try {
      // Direct usage of firestore instance
      const orgsCollection = collection(firestore, 'organizations');
      const q = query(orgsCollection, where('name', '==', name));

      const querySnapshot = await getDocs(q);

      return querySnapshot.empty;
    } catch (error) {
      log.error('Error checking organization name:', error);
      throw new Error('Failed to check organization name availability');
    }
  }

  /**
   * Validate organization form data
   * @param data - Organization form data to validate
   * @returns Array of validation errors
   */
  validateOrganizationForm(data: OrganizationFormData): string[] {
    const errors: string[] = [];

    // Validate name
    if (!data.name || data.name.trim() === '') {
      errors.push('Organization name is required');
    } else if (data.name.length < 2) {
      errors.push('Organization name must be at least 2 characters');
    }

    // Validate email if provided
    if (data.contact?.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(data.contact.email)) {
        errors.push('Contact email is invalid');
      }
    }

    // Validate website if provided
    if (data.contact?.website) {
      try {
        new URL(data.contact.website);
      } catch {
        errors.push('Contact website URL is invalid');
      }
    }

    return errors;
  }

  /**
   * Find an organization by name
   * @param name - Organization name to search for
   * @returns Promise with organization data or null if not found
   */
  async findOrganizationByName(name: string): Promise<Organization | null> {
    try {
      // Direct usage of firestore instance
      const orgsCollection = collection(firestore, 'organizations');
      const q = query(orgsCollection, where('name', '==', name), limit(1));

      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const orgDoc = querySnapshot.docs[0];
        return { id: orgDoc.id, ...orgDoc.data() } as Organization;
      }

      return null;
    } catch (error) {
      log.error('Error finding organization by name:', error);
      return null;
    }
  }

  // ===== ENTERPRISE STRUCTURE MANAGEMENT =====

  /**
   * Create a department in the organization
   */
  async createDepartment(organizationId: string, departmentData: Omit<OrganizationDepartment, 'id' | 'createdAt' | 'createdBy'>): Promise<OrganizationDepartment | null> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('User not authenticated');

      const now = new Date().toISOString();
      const department: OrganizationDepartment = {
        ...departmentData,
        id: uuidv4(),
        createdAt: now,
        createdBy: currentUser.uid,
      };

      // Add to subcollection for better organization
      await addDoc(collection(firestore, 'organizations', organizationId, 'departments'), department);

      // Update organization with department reference
      const org = await this.getOrganizationById(organizationId);
      if (org) {
        const departments = org.enterpriseStructure?.departments || [];
        departments.push(department);
        
        await this.updateOrganization(organizationId, {
          enterpriseStructure: {
            ...org.enterpriseStructure,
            departments,
          },
        });
      }

      return department;
    } catch (error) {
      log.error('Error creating department:', error);
      return null;
    }
  }

  /**
   * Get departments for an organization
   */
  async getDepartments(organizationId: string): Promise<OrganizationDepartment[]> {
    try {
      const departmentsQuery = query(
        collection(firestore, 'organizations', organizationId, 'departments'),
        where('isActive', '==', true),
        orderBy('name')
      );
      
      const snapshot = await getDocs(departmentsQuery);
      const departments: OrganizationDepartment[] = [];

      snapshot.forEach(doc => {
        departments.push({ id: doc.id, ...doc.data() } as OrganizationDepartment);
      });

      return departments;
    } catch (error) {
      log.error('Error getting departments:', error);
      return [];
    }
  }

  /**
   * Get organization members with their department assignments
   */
  async getMembersWithDepartments(organizationId: string): Promise<(OrganizationMember & { department?: OrganizationDepartment; role: OrganizationRole })[]> {
    try {
      const members = await this.getOrganizationMembers(organizationId);
      const departments = await this.getDepartments(organizationId);
      
      // Create department lookup map
      const departmentMap = new Map(departments.map(dept => [dept.managerId, dept]));
      
      return members.map(member => ({
        ...member,
        department: departmentMap.get(member.userId),
        role: member.role as OrganizationRole,
      }));
    } catch (error) {
      log.error('Error getting members with departments:', error);
      return [];
    }
  }

  /**
   * Assign a user to a department as manager
   */
  async assignDepartmentManager(organizationId: string, departmentId: string, userId: string): Promise<boolean> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('User not authenticated');

      // Get user details
      const userDoc = await getDoc(doc(firestore, 'users', userId));
      if (!userDoc.exists()) throw new Error('User not found');
      
      const userData = userDoc.data();

      // Update department
      await updateDoc(doc(firestore, 'organizations', organizationId, 'departments', departmentId), {
        managerId: userId,
        manager: {
          id: userId,
          name: userData.displayName || 'Unknown',
          email: userData.email || '',
        },
        updatedAt: new Date().toISOString(),
      });

      // Update user's role to department manager if not already higher
      const membership = await this.getMemberRole(organizationId, userId);
      if (membership && ![OrganizationRole.OWNER, OrganizationRole.ADMIN].includes(membership)) {
        await this.updateMemberRole(organizationId, userId, OrganizationRole.DEPARTMENT_MANAGER);
      }

      return true;
    } catch (error) {
      log.error('Error assigning department manager:', error);
      return false;
    }
  }

  /**
   * Get member role in organization
   */
  async getMemberRole(organizationId: string, userId: string): Promise<OrganizationRole | null> {
    try {
      const members = await this.getOrganizationMembers(organizationId);
      const member = members.find(m => m.userId === userId);
      return member ? member.role as OrganizationRole : null;
    } catch (error) {
      log.error('Error getting member role:', error);
      return null;
    }
  }

  /**
   * Get users eligible for department management (admin level or above)
   */
  async getEligibleManagers(organizationId: string): Promise<OrganizationMember[]> {
    try {
      const members = await this.getOrganizationMembers(organizationId);
      return members.filter(member => 
        [OrganizationRole.OWNER, OrganizationRole.ADMIN, OrganizationRole.DEPARTMENT_MANAGER, OrganizationRole.FINANCE_MANAGER]
          .includes(member.role as OrganizationRole)
      );
    } catch (error) {
      log.error('Error getting eligible managers:', error);
      return [];
    }
  }
}

export default new OrganizationService();